import {
  useCallback,
  useEffect,
  useState
} from "react";

import {
  getCategoriesApi,
  getCategoryApi,
  createCategoryApi,
  updateCategoryApi,
  deleteCategoryApi
} from "../../../api/categories/categories.api";


const useCategories = ({
  activeOnly = false
} = {}) => {
  const [categories, setCategories] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState(null);


  /*
  |--------------------------------------------------------------------------
  | CLEAR ERROR
  |--------------------------------------------------------------------------
  */

  const clearError = useCallback(() => {
    setError(null);
  }, []);


  /*
  |--------------------------------------------------------------------------
  | GET CATEGORIES
  |--------------------------------------------------------------------------
  */

  const fetchCategories =
    useCallback(async () => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await getCategoriesApi(activeOnly);

        console.log(
          "GET /categories response:",
          response
        );

        const categoryList =
          Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

        setCategories(categoryList);

        return categoryList;
      } catch (error) {
        console.error(
          "Failed to fetch categories:",
          error
        );

        setError(error);
        setCategories([]);

        throw error;
      } finally {
        setLoading(false);
      }
    }, [activeOnly]);


  /*
  |--------------------------------------------------------------------------
  | LOAD CATEGORIES
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);


  /*
  |--------------------------------------------------------------------------
  | GET SINGLE CATEGORY
  |--------------------------------------------------------------------------
  */

  const fetchCategory =
    useCallback(async (id) => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await getCategoryApi(id);

        return response?.data?.data;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    }, []);


  /*
  |--------------------------------------------------------------------------
  | CREATE CATEGORY
  |--------------------------------------------------------------------------
  */

  const createCategory =
    useCallback(async (data) => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await createCategoryApi(data);

        const createdCategory =
          response?.data?.data;

        setCategories(
          (currentCategories) => [
            createdCategory,
            ...currentCategories
          ]
        );

        return createdCategory;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    }, []);


  /*
  |--------------------------------------------------------------------------
  | UPDATE CATEGORY
  |--------------------------------------------------------------------------
  */

  const updateCategory =
    useCallback(async (id, data) => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await updateCategoryApi(
            id,
            data
          );

        const updatedCategory =
          response?.data?.data;

        setCategories(
          (currentCategories) =>
            currentCategories.map(
              (category) =>
                String(category.id) ===
                String(id)
                  ? updatedCategory
                  : category
            )
        );

        return updatedCategory;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    }, []);


  /*
  |--------------------------------------------------------------------------
  | DELETE CATEGORY
  |--------------------------------------------------------------------------
  */

  const deleteCategory =
    useCallback(async (id) => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await deleteCategoryApi(id);

        setCategories(
          (currentCategories) =>
            currentCategories.filter(
              (category) =>
                String(category.id) !==
                String(id)
            )
        );

        return response?.data;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    }, []);


  return {
    categories,
    loading,
    error,

    fetchCategories,
    fetchCategory,

    createCategory,
    updateCategory,
    deleteCategory,

    clearError
  };
};


export default useCategories;

export {
  useCategories
};